import { _ } from "../../polyglot/polyglot.js";
import { event } from "@foretagsplatsen/yaem";
import {
	extend,
	getPathValue,
	setPathValue,
} from "../../helpers/object-util.js";
import { getResult } from "../../helpers/functional-util.js";
import { router, widget } from "@foretagsplatsen/widgetjs";
import controlGroup from "./controlGroup.js";
import jQuery from "jquery";

// Horizontal form:
//
// Form fields are automatically wrapped in a controlGroup and rendered.
//
// This will Right align labels and float them to the left to make them appear on
// the same line as fields.
//
/**
 * Widget rendering a form.
 */
export default function formWidget(spec, my) {
	my ||= {};
	spec ||= {};

	const showCancel = spec.showCancel === undefined ? true : spec.showCancel;

	const that = widget(spec, my);

	that.fields = []; // all registered fields(controls)

	that.onSubmit = event();

	const model = spec.model || {};
	const patch = {}; // modified values

	const saveAction = spec.saveAction || (() => {});
	const saveLabel = spec.saveLabel || _("Save");

	const cancelAction = spec.cancelAction || (() => router.getRouter().back());
	const cancelLabel = spec.cancelLabel || _("Cancel");

	const labelClass = spec.labelClass;
	const inputClass = spec.inputClass;
	const formClass = spec.klass ?? "";
	const directionClass = spec.vertical ? "form-vertical" : "form-horizontal";
	const cancelClass = spec.cancelClass || "";
	const cancelAsButton = spec.cancelAsButton;
	const showActions =
		spec.showActions === undefined ? true : spec.showActions;
	const dataTestName = spec.dataTestName || "";
	const validationErrorAction = spec.validationErrorAction || (() => {});

	my.objects = []; // content to render inside form
	my.extraActions = spec.extraActions || [];
	my.validateOnBlur = spec.validateOnBlur !== false;

	that.isEmpty = function () {
		return !that.fields.length;
	};

	// Append object to form brush. See [htmlCanvas.js](htmlCanvas.html) for
	// valid objects
	that.append = function (object) {
		if (my.form) {
			my.form.render(object);
		}

		my.objects.push(object);
	};

	// Appends all arguments to form brush. See ´append()´
	that.render = function (...args) {
		for (const arg of args) {
			that.append(arg);
		}
		return that;
	};

	// Validate all enabled fields and return those invalid
	that.invalidFields = function () {
		return that.fields.filter(
			(field) => field.enabled() && !field.validate(),
		);
	};

	that.isValid = function () {
		return that.fields.every(
			(field) => !field.enabled() || field.isValid(),
		);
	};

	// Form is invalid if any field is invalid
	that.validate = function () {
		// test for accessor and patch uses
		my.checkPatchAndAccessors();

		return that.invalidFields().length === 0;
	};

	that.cancel = function () {
		my.resetFieldValues();
		cancelAction();
	};

	that.submit = function () {
		// only save if valid
		if (!that.validate()) {
			validationErrorAction();
			that.invalidFields()[0].asJQuery().get(0)?.scrollIntoView();
			return;
		}

		// Read values from all enabled fields and write to patch
		that.fields.forEach((field) => {
			if (!field.enabled()) {
				return;
			}
			// extract into a proper method
			if (field.path) {
				setPathValue(patch, field.path, field.getValue());
			} else if (field.mutator) {
				// write using the mutator if the field is not readonly
				field.mutator.call(field.model, field.getValue());
			}
		});

		saveAction(that.getObjectToSave());
		that.onSubmit.trigger();
	};

	that.legend = function (label, description) {
		function legend(html) {
			const element = html.legend(
				{
					klass: "section-title",
				},
				label,
			);
			if (description !== undefined) {
				element.render(
					html.p(
						{
							klass: "suppressed",
						},
						" ",
						description,
					),
				);
			}
		}

		that.append(legend);
	};

	that.renderContentOn = function (html) {
		my.form = html.form(
			{
				class: ["form", formClass, directionClass],
				"data-test": "form-horizontal",
			},
			my.objects,
			my.renderFormActionsOn,
		);

		my.form.submit(function () {
			jQuery(":focus").blur();
			that.submit(this);
			return false;
		});
	};

	my.renderFormActionsOn = function (html) {
		if (!showActions) {
			return;
		}

		html.div(
			{
				klass: [
					"form-actions",
					"btn-toolbar",
					"text-right",
					spec.actionsClass,
				],
			},
			my.renderActionsOn,
		);
	};

	my.renderActionsOn = function (html) {
		my.renderExtraActionsOn(html);

		if (showCancel) {
			if (cancelAsButton) {
				html.span(
					html
						.a(
							{
								klass: ["btn", "cancel", cancelClass],
								"data-test": "cancel",
							},
							cancelLabel,
						)
						.click(that.cancel),
					my.renderSaveButtonOn,
				);
			} else {
				html.span(
					_("{save} or {cancel}", {
						save: my.renderSaveButtonOn,
						cancel: html
							.a(
								{
									class: ["cancel", cancelClass],
									"data-test": "cancel",
								},
								cancelLabel,
							)
							.click(that.cancel),
					}),
				);
			}
		} else {
			html.span(my.renderSaveButtonOn);
		}
	};

	my.renderExtraActionsOn = function (html) {
		const buttons = getResult(my.extraActions);

		if (buttons.length === 0) {
			return;
		}

		html.div({ class: "btn-group" }, buttons);
	};

	my.renderSaveButtonOn = function (html) {
		html.button(
			{
				"data-test": dataTestName,
				klass: "btn btn-primary",
				type: "submit",
			},
			saveLabel,
		);
	};

	const groups = {};

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	that.group = function (spec) {
		spec ||= {};

		if (spec.group && groups[spec.group]) {
			return groups[spec.group];
		}

		const group = controlGroup({
			labelClass: spec.labelClass || labelClass,
			inputClass,
			label: spec.label,
			labelfor: spec.labelfor,
			helpCallback: spec.helpCallback,
		});

		groups[spec.group || my.nextId()] = group;
		that.render(group);

		return group;
	};

	// eslint-disable-next-line no-shadow -- bad practice, we should fix this
	that.addField = function (field, spec) {
		// Wrap field in a control group
		that.group(spec).appendField(field);
		return my.registerField(field);
	};

	that.getObjectToSave = function () {
		// We have 2 different strategies:
		// - the patch was used (dot notation)
		// - accessors/mutators were used and we ignore the patch

		const usePatch = Object.keys(patch).length !== 0;

		if (usePatch) extend(model, patch);

		return model;
	};

	my.checkPatchAndAccessors = function () {
		let usePath;
		let useMutator;

		that.fields.forEach((each) => {
			if (each.path) {
				usePath = true;
			} else if (each.mutator) {
				useMutator = true;
			}
			if (usePath && useMutator) {
				throw "Form mixing dot notation and mutators. Cowardly abandoning.";
			}
		});
	};

	/**
	 * Update each field content by reading the model.
	 */
	my.resetFieldValues = function () {
		that.fields.forEach((field) => {
			my.resetFieldValue(field, true);
		});

		that.update();
	};

	/**
	 * Get the value from the model and change the field's content
	 * accordingly.
	 */
	my.resetFieldValue = function (field, preventValidation) {
		let value;

		if (field.accessor) {
			value = field.accessor.call(field.model);
		} else {
			value = getPathValue(model, field.path);
		}

		field.setValue(value, preventValidation);
	};

	my.registerField = function (field) {
		if (
			!field ||
			!(field.attribute || field.accessor) ||
			!field.getValue ||
			!field.setValue ||
			!field.validate ||
			!field.enabled
		) {
			throw "Invalid field";
		}

		// attach patch/model path
		if (field.attribute) {
			field.path = field.attribute.split(".");
		}

		if (!field.getValue()) {
			my.resetFieldValue(field);
		}

		that.fields.push(field);
		field.setValidateOnBlur(my.validateOnBlur);
		return field;
	};

	return that;
}
